/**
 * The one move run (bata-reqs ADR 9002 stage 3, platform ADR 9049/9050) — what the Compacting app's
 * pages share: the URL key, the paths, the words for every code a line or a report carries, and the
 * KPI arithmetic. Pure: no React, no request.
 *
 * **One run, one list.** Since the release that deploys the one run there is one simulation, one
 * proposal set and one approval process for every store-to-store move (stage 3 ruling 1): full-price
 * transfers, size-run repair, clearance consolidation and the forced lifecycle lines are lines of one
 * merged list, forced lines first, then every other move by value (ruling 4). A line carries its
 * article's state — replenishment or compacting, from the warehouse's units against the market's
 * threshold (ruling 3) — and its state on the list: proposed, held (a cap or a joint rule kept it back
 * this week, in its place) or refused (a candidate the run refused by name).
 *
 * **The run is its own key.** A move run's id is not an in-season cycle run's, so the pages read
 * `?moveRunId=`, never `?runId=`: the address bar carries every key across screens, and a cycle run's
 * id would name no move run.
 */

import type { MoveListItem, MoveRunKpi } from '@/api/generated';

/** The URL key a Compacting one-run page reads its run from. */
export const MOVE_RUN_ID_PARAM = 'moveRunId';

/** The one merged list of a run: decided here, one approval process (stage 3 ruling 2). */
export const COMPACTING_MOVES_PATH = '/inseason/compacting-moves';
/** The run's report: KPIs per state, ownership, gates, invariants, refusals by reason. */
export const COMPACTING_REPORT_PATH = '/inseason/compacting-report';

/** The memory scope of the run default (DEC-99, `docs/adr/9039`): the run names its own market. */
export const MOVE_RUN_SCOPE = { kind: 'compacting.move_run' } as const;

/** Rows a page of the list draws (ADR 9034's fifty). */
export const MOVE_PAGE_SIZE = 50;

/** The list's three states (`MoveState`). */
export const STATE_PROPOSED = 'proposed';
export const STATE_HELD = 'held';
export const STATE_REFUSED = 'refused';
/** The states a planner decides: a held line keeps its place and may be rejected or deferred. */
export const DECIDABLE_STATES = [STATE_PROPOSED, STATE_HELD] as const;

/** The `disposition` filter value for lines no planner has decided yet. */
export const UNDECIDED = 'undecided';

/** The run's id from a search string; `null` when absent or blank. Pure. */
export function readMoveRunId(search: string): string | null {
  const value = new URLSearchParams(search).get(MOVE_RUN_ID_PARAM);
  return value === null || value.trim() === '' ? null : value;
}

/**
 * A decimal amount as the platform sends it (a string, ADR 6038's money) as a number. An absent or
 * unreadable amount is `null` — never a nought: a line that was not valued is not worth nothing.
 */
export function amountOf(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A KPI total as a number, always (owner ruling 2026-10-14: «Totals always a number»). The platform
 * already sums what is recorded and answers 0 over nothing; a total it sends that does not parse is
 * still shown as 0, and the coverage (lines valued against unvalued) is said in the (i), never as
 * «Sin valor».
 */
export function totalOf(value: string | number | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  return amountOf(value) ?? 0;
}

/** How many of a KPI block's lines carry a value, and how many in all — what the (i) says. */
export interface KpiCoverage {
  readonly valued: number;
  readonly lines: number;
}

/** The coverage of a KPI block: valued lines over valued plus unvalued. Pure. */
export function kpiCoverage(kpi: Pick<MoveRunKpi, 'lines_valued' | 'lines_unvalued'>): KpiCoverage {
  const valued = Math.max(0, kpi.lines_valued);
  return { valued, lines: valued + Math.max(0, kpi.lines_unvalued) };
}

/** A line's article state words. */
export const STOCK_OWNER_KEY: Readonly<Record<string, string>> = {
  replenishment: 'inseason.compacting.owner.replenishment',
  compacting: 'inseason.compacting.owner.compacting',
};

/** A line's state on the list. */
export const STATE_KEY: Readonly<Record<string, string>> = {
  [STATE_PROPOSED]: 'inseason.compacting.state.proposed',
  [STATE_HELD]: 'inseason.compacting.state.held',
  [STATE_REFUSED]: 'inseason.compacting.state.refused',
};

/** What a line does — its purpose (`planning.objective.move_run.PURPOSES`). */
export const PURPOSE_KEY: Readonly<Record<string, string>> = {
  inseason_transfer: 'inseason.compacting.purpose.inseasonTransfer',
  size_run_repair: 'inseason.compacting.purpose.sizeRunRepair',
  door_consolidated: 'inseason.compacting.purpose.doorConsolidated',
  zona_sales_withdrawal: 'inseason.compacting.purpose.withdrawal',
  outlet_exit_transfer: 'inseason.compacting.purpose.outletExit',
};

/** Why a line is held this week (`HOLD_CODES`). */
export const HOLD_KEY: Readonly<Record<string, string>> = {
  move_cap_deferred: 'inseason.compacting.hold.moveCap',
  outbound_units_cap_deferred: 'inseason.compacting.hold.outboundUnitsCap',
  // bata-platform ADR 9058: the line's destination is not one of the lanes its origin's MILP opened.
  outbound_destinations_cap_deferred: 'inseason.compacting.hold.outboundDestinationsCap',
  stock_claimed: 'inseason.compacting.hold.stockClaimed',
  donor_floor: 'inseason.compacting.hold.donorFloor',
  run_damage_uncharged: 'inseason.compacting.hold.runDamageUncharged',
  c2_presentation_minimum: 'inseason.compacting.hold.presentationMinimum',
  // Platform ADR 9051 (owner ruling 2026-11-01): a consolidation line whose lane's week's shipment,
  // every purpose together, is under the market's `allocation.minimum_transport_units`.
  consolidation_shipment_below_minimum: 'inseason.compacting.hold.shipmentBelowMinimum',
};

/** Why a candidate was refused (`REFUSAL_CODES` and the shared valuation's own codes). */
export const REFUSAL_KEY: Readonly<Record<string, string>> = {
  stock_owner_unresolved: 'inseason.compacting.refusal.ownerUnresolved',
  purpose_not_of_stock_owner: 'inseason.compacting.refusal.notTheStatesMove',
  forced_line_replenishment_owned: 'inseason.compacting.refusal.forcedReplenishmentOwned',
  option_terms_absent: 'inseason.compacting.refusal.termsAbsent',
  expected_price_absent: 'inseason.compacting.refusal.priceAbsent',
  position_absent: 'inseason.compacting.refusal.positionAbsent',
  units_not_on_hand: 'inseason.compacting.refusal.notOnHand',
  lane_not_costed: 'inseason.compacting.refusal.laneNotCosted',
  d2_not_met: 'inseason.compacting.refusal.d2NotMet',
  donor_floor: 'inseason.compacting.refusal.donorFloor',
  transfer_uneconomic: 'inseason.compacting.refusal.uneconomic',
  door_under_forced_line: 'inseason.compacting.refusal.doorUnderForcedLine',
  forced_nothing_on_hand: 'inseason.compacting.refusal.forcedNothingOnHand',
  // Platform ADR 9049 amendment 2026-10-31: a size the run could not put in the snapshot's spelling.
  size_key_untranslated: 'inseason.compacting.refusal.sizeKeyUntranslated',
  'planning.objective.move_value.landed_cost_missing': 'inseason.compacting.refusal.landedCostMissing',
  'planning.objective.move_value.landed_cost_provenance_not_accepted': 'inseason.compacting.refusal.landedCostProvenance',
  'planning.objective.move_value.mixed_currency': 'inseason.compacting.refusal.mixedCurrency',
};

/** The run's gates (platform locale `compacting.gate.*`, ADR 9050). */
export const GATE_KEY: Readonly<Record<string, string>> = {
  stock_batch_in_force: 'inseason.compacting.gate.stockBatchInForce',
  dc_stock_measured: 'inseason.compacting.gate.dcStockMeasured',
  threshold_in_force: 'inseason.compacting.gate.thresholdInForce',
  valuation_shared: 'inseason.compacting.gate.valuationShared',
  run_completed: 'inseason.compacting.gate.runCompleted',
};

/** The record's invariants, recomputed on read (platform locale `compacting.invariant.*`). */
export const INVARIANT_KEY: Readonly<Record<string, string>> = {
  header_counts_agree: 'inseason.compacting.invariant.headerCountsAgree',
  ownership_whole: 'inseason.compacting.invariant.ownershipWhole',
  line_owner_is_article_owner: 'inseason.compacting.invariant.lineOwnerIsArticleOwner',
  forced_ranked_first: 'inseason.compacting.invariant.forcedRankedFirst',
  one_currency: 'inseason.compacting.invariant.oneCurrency',
};

/** The «Perché» workbook's five sheets, in the platform's order (`MoveRunSheet`, `GET …/workbook`). */
export const WORKBOOK_SHEETS = ['moves', 'held', 'refused', 'ledger', 'parameters'] as const;
export type WorkbookSheetId = (typeof WORKBOOK_SHEETS)[number];

/** Each sheet's title (platform locale `compacting.workbookSheet.*`). ≤31 characters once translated. */
export const WORKBOOK_SHEET_KEY: Readonly<Record<WorkbookSheetId, string>> = {
  moves: 'inseason.compacting.workbookSheet.moves',
  held: 'inseason.compacting.workbookSheet.held',
  refused: 'inseason.compacting.workbookSheet.refused',
  ledger: 'inseason.compacting.workbookSheet.ledger',
  parameters: 'inseason.compacting.workbookSheet.parameters',
};

/**
 * A code as words: the catalogue's, or — for a code this build does not name — the code itself, so
 * a new code reads as what it is rather than disappearing. Pure; `translate` is the caller's.
 */
export function codeWords(table: Readonly<Record<string, string>>, code: string | null | undefined, translate: (key: string) => string): string | null {
  if (code === null || code === undefined || code === '') return null;
  const key = table[code];
  return key === undefined ? code : translate(key);
}

/** The reason a line is what it is: a refused candidate's refusal, a held line's hold, else none. Pure. */
export function lineReasonCode(line: Pick<MoveListItem, 'state' | 'refusal_code' | 'hold_reason_code'>): string | null {
  if (line.state === STATE_REFUSED) return line.refusal_code;
  if (line.state === STATE_HELD) return line.hold_reason_code;
  return null;
}

/** The words for {@link lineReasonCode}, from the table its state reads. Pure. */
export function lineReasonWords(line: Pick<MoveListItem, 'state' | 'refusal_code' | 'hold_reason_code'>, translate: (key: string) => string): string | null {
  const code = lineReasonCode(line);
  return codeWords(line.state === STATE_REFUSED ? REFUSAL_KEY : HOLD_KEY, code, translate);
}

/** Whether a planner may still decide the line: proposed or held, and undecided. Pure. */
export function isUndecided(line: Pick<MoveListItem, 'state' | 'disposition'>): boolean {
  return (line.state === STATE_PROPOSED || line.state === STATE_HELD) && (line.disposition === null || line.disposition === '');
}
